<template>
  <div class="flex gap-2">
    <CIcon
      class="font-900 color-primary3 mt-6 cursor-pointer flex-shrink-0"
      data-handle-group
      icon="grip-dots-vertical"
      size="20"
    />

    <div class="border-style-solid border-primary4 border-width-1 rounded-2 pa-4 flex-auto overflow-auto">
      <CCollapse v-model:expandedNames="expandedGroups" arrow-placement="left">
        <CCollapseItem :name="group.id">
          <template #header>
            <CTableInput
              :always-display-edit-icon="true"
              :class="{ 'w-full max-w-[400px]': getInputState(activeInputId === group.id) === 'edit' }"
              :max-length="64"
              :model-value="group.name"
              :state="getInputState(activeInputId === group.id)"
              value-class="color-text font-size-[18px] font-500"
              @cancel="clearActiveInput()"
              @edit="setActiveInput(group.id)"
              @update:modelValue="editGroupName(group.id, $event)"
            />
          </template>

          <template #header-extra>
            <div class="flex gap-2 items-center">
              <!-- Commented out until other actions will be implemented  -->
              <!--              <CDropdown-->
              <!--                :options="getActionsMenu()"-->
              <!--                :themeOverrides="dropdownThemeOverrides"-->
              <!--                trigger="click"-->
              <!--                @select="handleActions"-->
              <!--              >-->
              <CButton
                v-tooltip="t('canvasUI.agendaSelector.addContent')"
                circle
                strong
                type="secondary"
                @click="addItem(props.group.id, filters)"
              >
                <template #icon>
                  <CIcon class="color-primary3" icon="plus" size="16" />
                </template>
              </CButton>
              <!--              </CDropdown>-->

              <CButton
                v-tooltip="t('canvasUI.agendaSelector.deleteAgendaItem')"
                circle
                secondary
                strong
                type="error"
                @click.stop="deleteGroup(group.id, group.name)"
              >
                <template #icon>
                  <CIcon icon="trash" size="16" />
                </template>
              </CButton>
            </div>
          </template>

          <VueDraggable
            class="flex flex-col"
            :group="{
              name: 'items',
            }"
            handle="[data-handle-item]"
            :model-value="group.items"
            v-bind="DragConfig"
            @update:model-value="reorderItems(group.id, $event)"
          >
            <div v-for="(item, index) in group.items" :key="item.id" class="flex">
              <div v-if="index !== 0" class="border-b-solid border-primary6"></div>

              <div class="flex gap-2 ma-2 flex-auto overflow-auto">
                <CIcon
                  class="color-primary4 flex-shrink-0 cursor-pointer font-900 self-center"
                  data-handle-item
                  icon="grip-dots-vertical"
                  size="20"
                />

                <AgendaItem
                  :content="item.content"
                  :disabled-move-to-group="agenda.groups.length === 1"
                  :filters="filters"
                  :is-first="index === 0"
                  :is-last="index === group.items.length - 1"
                  :type="item.type"
                  @delete="deleteItem(group.id, item)"
                  @edit="editItem(group.id, item, filters)"
                  @moveDown="moveInDirection(group.id, item, 1)"
                  @moveToGroup="openMoveItemModal({ groupId: group.id, item })"
                  @moveUp="moveInDirection(group.id, item, -1)"
                />
              </div>
            </div>
          </VueDraggable>

          <div
            v-if="!group?.items || group.items.length === 0"
            class="bg-primary6 flex pa-4 justify-center items-center rounded-2"
          >
            <!-- Commented out until other actions will be implemented  -->
            <!--            <CDropdown-->
            <!--              :options="getActionsMenu()"-->
            <!--              :themeOverrides="dropdownThemeOverrides"-->
            <!--              trigger="click"-->
            <!--              @click="addItem(props.group.id)"-->
            <!--              @select="handleActions"-->
            <!--            >-->
            <CButton
              class="border-style-solid border-primary5 border-width-1"
              iconPlacement="left"
              type="secondary"
              @click="addItem(props.group.id, filters)"
            >
              <template #icon>
                <CIcon class="color-primary3" icon="plus" size="16" />
              </template>

              {{ t('canvasUI.agendaSelector.addContent') }}
            </CButton>
            <!--            </CDropdown>-->
          </div>
        </CCollapseItem>
      </CCollapse>
    </div>
  </div>
</template>

<script setup lang="tsx">
import { nextTick, ref } from 'vue'
import { VueDraggable } from 'vue-draggable-plus'
// import type { ValuesOf } from '@tiptap/core'
import { useI18n } from 'vue-i18n'
import CCollapseItem from '@lib/components/CCollapse/CCollapseItem.vue'
import CTableInput from '@lib/components/CTableInput/CTableInput.vue'
import AgendaItem from '@agenda-selector/components/AgendaItem.vue'
// import { ContentType } from '@agenda-selector/constants'
import CButton from '@lib/components/CButton/CButton.vue'
// import CDropdown from '@lib/components/CDropdown/CDropdown.vue'
import CCollapse from '@lib/components/CCollapse/CCollapse.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import { useTableInput } from '@agenda-selector/composables/table-input.use'
import type { AgendaGroup, AgendaItem as AgendaItemType } from '@agenda-selector/types/agenda.type'
import { useAppStore } from '@agenda-selector/stores/app.store'
import { useMoveItemModal } from '@agenda-selector/composables/move-item-modal.use'
import { DragConfig } from '@agenda-selector/constants'
// import { useApplicationBlockSelectorModal } from '@agenda-selector/composables/application-block-selector-modal.use'
// import { dropdownThemeOverrides } from '@agenda-selector/utils/theme-overrides.util'

const props = defineProps<{
  group: AgendaGroup
  filters?: Record<string, any>
}>()

const { t } = useI18n()
const { open: openMoveItemModal } = useMoveItemModal()
// const { open: openApplicationBlockSelectorModal } = useApplicationBlockSelectorModal()
const { getInputState, setActiveInput, clearActiveInput, activeInputId } = useTableInput()
const {
  agenda,
  expandedGroups,
  editItem,
  deleteItem,
  deleteGroup,
  updateGroupName,
  updateItems,
  updateGroups,
  addItem,
} = useAppStore()

const itemOrderChangeQueue = ref<{ [groupId: string]: AgendaItemType[] }>({})

// function getActionsMenu() {
//   return [
//     {
//       label: () => <div class="font-size-4">{t('canvasUI.agendaSelector.contentType.collection')}</div>,
//       key: ContentType.Collection,
//       icon: () => <CIcon icon="file-circle-plus" />,
//     },
//     {
//       label: () => <div class="font-size-4">{t('canvasUI.agendaSelector.contentType.application')}</div>,
//       key: ContentType.Application,
//       icon: () => <CIcon icon="regular-rocket-circle-plus" faType="fa-kit" />,
//     },
//     {
//       label: () => <div class="font-size-4">{t('canvasUI.agendaSelector.contentType.block')}</div>,
//       key: ContentType.Block,
//       icon: () => <CIcon icon="regular-objects-column-circle-plus" faType="fa-kit" />,
//     },
//   ]
// }

// function handleActions(key: ValuesOf<ContentType>) {
//   switch (key) {
//     case ContentType.Collection:
//       addItem(props.group.id)
//       break
//     case ContentType.Block:
//       openApplicationBlockSelectorModal(ContentType.Block)
//       break
//     case ContentType.Application:
//       openApplicationBlockSelectorModal(ContentType.Application)
//       break
//     default:
//       break
//   }
// }

const moveInDirection = (groupId: string, item: AgendaItemType, direction: -1 | 1) => {
  const group = agenda.value.groups.find((g) => g.id === groupId)
  const items = group?.items

  if (!items) return

  const orderedArray = moveItemInArray([...items], group?.items.indexOf(item), group?.items.indexOf(item) + direction)

  updateItems(groupId, orderedArray)
}

const editGroupName = (groupId: string, name: string) => {
  updateGroupName(groupId, name)
  clearActiveInput()
}

const reorderItems = (groupId: string, items: AgendaItemType[]) => {
  itemOrderChangeQueue.value[groupId] = items

  updateGroups(
    agenda.value.groups.map((group) =>
      group.id in itemOrderChangeQueue.value ? { ...group, items: itemOrderChangeQueue.value[group.id] } : group,
    ),
  )

  nextTick(() => (itemOrderChangeQueue.value = {}))
}

function moveItemInArray(array: AgendaItemType[], oldIndex: number, newIndex: number) {
  const [item] = array.splice(oldIndex, 1)
  array.splice(Math.min(newIndex, array.length), 0, item)

  return array
}
</script>

<style lang="scss" scoped>
:deep(.n-collapse-item__header-main) {
  font-size: 15px;
  line-height: 18px !important;
  letter-spacing: 0.15px;
  padding: 8px !important;

  .n-collapse-item-arrow {
    margin-left: 0 !important;
    font-size: 12px !important;
    margin-right: 8px !important;
  }
}
</style>
